import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, thumbUrl } from "../lib/api";
import { useAuth } from "../lib/auth";
import { useI18n, fieldLabel, attrLabel } from "../lib/i18n";
import { Badge } from "../components/ui/badge";
import { Progress } from "../components/ui/progress";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../components/ui/dialog";
import { Sparkles, FileText, ShieldCheck, Mail, Phone, Building2, HeartHandshake, AlertTriangle, ArrowRight, Trash2, GitCompare, Check, ChevronDown, ChevronRight } from "lucide-react";

function CompareDialog({ m, open, onOpenChange }) {
  const { t } = useI18n();
  const a = m.ante_mortem;
  const p = m.post_mortem;
  const matched = new Set(m.matched_fields || []);
  const rng = (lo, hi) => (lo == null && hi == null ? "—" : lo === hi ? `${lo}` : `${lo ?? "?"}–${hi ?? "?"}`);
  const v = (x) => (x == null || x === "" ? "—" : x);
  const rows = [
    ["form.gender", a.gender !== "unknown" && a.gender ? t("form.gender." + a.gender) : "—",
     p.gender !== "unknown" && p.gender ? t("form.gender." + p.gender) : "—",
     matched.has("gender") || matched.has("gender_partial")],
    ["form.age", rng(a.age_min, a.age_max), rng(p.age_min, p.age_max), matched.has("age")],
    ["form.height", a.height_cm ? `${a.height_cm} cm` : "—", p.height_cm ? `${p.height_cm} cm` : "—", matched.has("height")],
    ["form.hair", v(attrLabel(t, a.hair_color)), v(attrLabel(t, p.hair_color)), matched.has("hair_color")],
    ["form.eye", v(attrLabel(t, a.eye_color)), v(attrLabel(t, p.eye_color)), matched.has("eye_color")],
    ["form.ethnicity", v(a.ethnicity), v(p.ethnicity), matched.has("ethnicity")],
    ["form.country", v(a.country), v(p.country), matched.has("country")],
    ["form.location", v(a.location), v(p.location), false],
    ["form.marks", v(a.distinguishing_marks), v(p.distinguishing_marks), matched.has("distinguishing_marks")],
  ];
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[85vh] max-w-3xl overflow-y-auto" data-testid="compare-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-lg text-[#1A2024]">{t("match.compare_title")}</DialogTitle>
          <DialogDescription className="sr-only">{t("match.compare_title")}</DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-[1fr_1fr] gap-3 sm:grid-cols-[140px_1fr_1fr]">
          <div className="hidden sm:block" />
          {[["ante", a], ["post", p]].map(([kind, c]) => (
            <div key={kind} className="flex items-center gap-3">
              <div className="h-14 w-14 flex-shrink-0 overflow-hidden rounded-md border border-[#E2E8F0] bg-[#F8F9FA]">
                {(c.photo_path || c.photo_paths?.[0]) && (
                  <img src={thumbUrl(c.photo_path || c.photo_paths[0])} alt="" className="h-full w-full object-cover" />
                )}
              </div>
              <div>
                <p className="font-body text-sm font-semibold text-[#1A2024]">
                  {kind === "ante" ? (c.full_name || c.reference_code) : c.reference_code}
                </p>
                <p className="font-body text-[11px] uppercase tracking-[0.12em]" style={{ color: kind === "ante" ? "#4A7C59" : "#1F3A5F" }}>
                  {kind === "ante" ? t("matches.ante") : t("matches.post")}
                </p>
              </div>
            </div>
          ))}
        </div>
        <div className="mt-2 overflow-hidden rounded-md border border-[#E2E8F0]">
          {rows.map(([key, av, pv, hit]) => (
            <div
              key={key}
              className={`grid grid-cols-[110px_1fr_1fr] gap-2 border-b border-[#EEF1F4] px-3 py-2 last:border-b-0 sm:grid-cols-[140px_1fr_1fr] ${hit ? "bg-[#4A7C59]/[0.08]" : "bg-white"}`}
              data-testid={`compare-row-${key.replace("form.", "")}`}
            >
              <span className="inline-flex items-center gap-1 font-body text-xs font-medium text-[#6B7F82]">
                {hit && <Check size={12} className="text-[#4A7C59]" />}
                {t(key)}
              </span>
              <span className="font-body text-sm text-[#1A2024]">{av}</span>
              <span className="font-body text-sm text-[#1A2024]">{pv}</span>
            </div>
          ))}
        </div>
        {m.circumstance_hits?.length > 0 && (
          <p className="font-body text-xs text-[#8a6a2e]">
            {t("matches.circ_hits")}: {m.circumstance_hits.join(", ")}
          </p>
        )}
      </DialogContent>
    </Dialog>
  );
}

export const FIELD_LABELS = {
  gender: "Gender",
  age: "Age range",
  country: "Country",
  height: "Height",
  hair_color: "Hair color",
  eye_color: "Eye color",
  ethnicity: "Ethnicity",
  distinguishing_marks: "Distinguishing marks",
  gender_partial: "Gender unknown (partial)",
  circumstances: "Circumstances",
}; // kept for backward compatibility; UI uses fieldLabel(t, field)

export function TimelineWarning({ ante, post }) {
  const { t } = useI18n();
  const lastSeen = ante?.event_date;
  const found = post?.event_date;
  return (
    <div
      className="mt-4 rounded-lg border border-[#C08A2D]/40 bg-[#C08A2D]/[0.08] p-4"
      data-testid="timeline-warning"
    >
      <div className="flex items-start gap-3">
        <AlertTriangle size={18} className="mt-0.5 flex-shrink-0 text-[#B26B00]" />
        <div>
          <div className="font-heading text-sm font-semibold text-[#8A5200]">{t("matches.timeline_warning_title")}</div>
          <p className="mt-1 font-body text-xs leading-relaxed text-[#7A5A2A]">{t("matches.timeline_warning_body")}</p>
        </div>
      </div>
      {(lastSeen || found) && (
        <div className="mt-3 grid gap-2 sm:grid-cols-[1fr_auto_1fr] sm:items-stretch" data-testid="timeline-detail">
          <div className="rounded-md border border-[#C08A2D]/30 bg-white/60 px-3 py-2" data-testid="timeline-last-seen">
            <div className="font-body text-[10px] uppercase tracking-[0.14em] text-[#B26B00]">{t("matches.date_last_seen")}</div>
            <div className="mt-0.5 font-heading text-sm font-semibold text-[#5C4212]">{lastSeen || "—"}</div>
          </div>
          <div className="hidden items-center justify-center text-[#B26B00] sm:flex">
            <ArrowRight size={18} />
          </div>
          <div className="rounded-md border border-[#C08A2D]/30 bg-white/60 px-3 py-2" data-testid="timeline-found">
            <div className="font-body text-[10px] uppercase tracking-[0.14em] text-[#B26B00]">{t("matches.date_found")}</div>
            <div className="mt-0.5 font-heading text-sm font-semibold text-[#5C4212]">{found || "—"}</div>
          </div>
        </div>
      )}
    </div>
  );
}

export function MiniCard({ c, kind }) {
  const { t } = useI18n();
  const title = kind === "ante" ? (c.full_name || c.reference_code || "Missing person") : (c.reference_code || "Unidentified case");
  const linkable = !c.is_person_file && c.id;
  const path = kind === "ante" ? `/ante-mortem/${c.id}` : `/post-mortem/${c.id}`;
  return (
    <div className="flex gap-3" data-testid={`match-mini-${kind}-${c.id}`}>
      <div className="h-20 w-20 flex-shrink-0 overflow-hidden rounded-md border border-[#E2E8F0] bg-[#F8F9FA]">
        {(c.photo_path || c.photo_paths?.[0]) ? (
          <img src={thumbUrl(c.photo_path || c.photo_paths[0])} alt="" className="h-full w-full object-cover" />
        ) : (
          <div className="grid h-full w-full place-items-center text-[#6B7F82]"><FileText size={18} /></div>
        )}
      </div>
      <div className="min-w-0 flex-1">
        {linkable ? (
          <Link to={path} className="font-heading text-sm font-semibold text-[#1A2024] hover:text-[#2E3A46] hover:underline" data-testid={`match-open-${kind}-${c.id}`}>
            {title}
          </Link>
        ) : (
          <div className="font-heading text-sm font-semibold text-[#1A2024]">{title}</div>
        )}
        {c.is_person_file && (
          <span className="mt-0.5 inline-flex items-center gap-1 rounded-full bg-[#4A7C59]/10 px-2 py-0.5 font-body text-[10px] font-medium text-[#4A7C59]" data-testid={`match-personfile-${c.id}`}>
            Combined file · {c.member_count} reports
          </span>
        )}
        <div className="font-body text-xs text-[#5C6873]">{c.country}{c.location ? `, ${c.location}` : ""}</div>
        <div className="font-body text-xs text-[#5C6873]">
          {c.gender !== "unknown" ? t("form.gender." + c.gender) : t("form.gender.unknown")} · age {c.age_min ?? "?"}-{c.age_max ?? "?"}
        </div>
        {kind === "post" && c.contributor_org && <div className="font-body text-xs font-medium text-[#4A7C59]" data-testid={`match-org-${c.id}`}>{t("case.submitted_by")} {c.contributor_org}</div>}
        {c.event_date && <div className="font-body text-xs text-[#5C6873]">{c.event_date}</div>}
      </div>
    </div>
  );
}

export const STATUS_META = {
  new: { text: "#5C6873", bg: "#F1F3F5", border: "#D7DCE1" },
  investigating: { text: "#1F5FBF", bg: "#1F5FBF14", border: "#1F5FBF40" },
  excluded: { text: "#9E4747", bg: "#9E474714", border: "#9E474740" },
  confirmed: { text: "#2E7D46", bg: "#2E7D4614", border: "#2E7D4640" },
};

export function StatusBadge({ status }) {
  const { t } = useI18n();
  const m = STATUS_META[status] || STATUS_META.new;
  return (
    <span
      className="inline-flex items-center gap-1.5 rounded-full border px-2.5 py-0.5 font-body text-[11px] font-semibold"
      style={{ color: m.text, backgroundColor: m.bg, borderColor: m.border }}
      data-testid={`match-status-${status}`}
      title={status === "new" ? t("match.new_hint") : undefined}
    >
      <span className="h-1.5 w-1.5 rounded-full" style={{ backgroundColor: m.text }} />
      {t(`review.status.${status}`)}
    </span>
  );
}

function fmtDate(iso) {
  try { return new Date(iso).toLocaleString(); } catch { return iso; }
}

export function MatchReview({ anteId, postId, review, onChange, canReview = true }) {
  const { t } = useI18n();
  const { user } = useAuth();
  const [status, setStatus] = useState(review?.status || "new");
  const [notes, setNotes] = useState(review?.notes || []);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);

  const apply = (data) => {
    setStatus(data.status);
    setNotes(data.notes || []);
    onChange && onChange(data);
  };

  const changeStatus = async (s) => {
    if (s === status || busy) return;
    setBusy(true);
    try {
      const r = await api.post("/matches/review/status", { ante_id: anteId, post_id: postId, status: s });
      apply(r.data);
    } finally { setBusy(false); }
  };

  const addNote = async () => {
    const v = text.trim();
    if (!v || busy) return;
    setBusy(true);
    try {
      const r = await api.post("/matches/review/note", { ante_id: anteId, post_id: postId, text: v });
      apply(r.data);
      setText("");
    } finally { setBusy(false); }
  };

  const deleteNote = async (noteId) => {
    if (busy) return;
    setBusy(true);
    try {
      const r = await api.post("/matches/review/note/delete", { ante_id: anteId, post_id: postId, note_id: noteId });
      apply(r.data);
    } finally { setBusy(false); }
  };

  return (
    <div className="mt-5 border-t border-[#E2E8F0] pt-4" data-testid={`match-review-${anteId}-${postId}`}>
      <div className="flex flex-wrap items-center gap-2">
        <span className="font-body text-xs font-medium uppercase tracking-[0.14em] text-[#6B7F82]">{t("review.status_label")}</span>
        {canReview && user?.role === "admin" ? (
          <div className="flex flex-wrap gap-1.5">
            {["new", "investigating", "excluded", "confirmed"].map((s) => {
              const active = s === status;
              const m = STATUS_META[s];
              return (
                <button
                  key={s}
                  type="button"
                  disabled={busy}
                  onClick={() => changeStatus(s)}
                  className="rounded-full border px-3 py-1 font-body text-xs font-medium transition-colors disabled:opacity-50"
                  style={active
                    ? { color: "#fff", backgroundColor: m.text, borderColor: m.text }
                    : { color: m.text, backgroundColor: "transparent", borderColor: m.border }}
                  data-testid={`match-set-status-${s}-${anteId}-${postId}`}
                >
                  {t(`review.status.${s}`)}
                </button>
              );
            })}
          </div>
        ) : (
          <StatusBadge status={status} />
        )}
      </div>
      {!canReview && (
        <p className="mt-2 font-body text-xs text-[#8A96A0]" data-testid={`match-review-readonly-${anteId}-${postId}`}>
          {t("review.read_only")}
        </p>
      )}
      {canReview && user?.role !== "admin" && (
        <p className="mt-2 font-body text-xs text-[#8A96A0]" data-testid={`match-status-admin-only-${anteId}-${postId}`}>
          {t("review.status_admin_only")}
        </p>
      )}

      <div className="mt-4">
        <div className="font-body text-xs font-medium uppercase tracking-[0.14em] text-[#6B7F82]">{t("review.log_title")}</div>
        <div className="mt-2 space-y-2" data-testid={`match-notes-${anteId}-${postId}`}>
          {notes.length === 0 && (
            <p className="font-body text-xs text-[#8A96A0]">{t("review.no_notes")}</p>
          )}
          {notes.map((n) => (
            <div key={n.id} className="rounded-md border border-[#E2E8F0] bg-[#F8F9FA] px-3 py-2" data-testid={`match-note-${n.id}`}>
              <div className="flex items-center justify-between gap-2">
                <span className="font-body text-xs font-semibold text-[#2E3A46]">{n.author_name}</span>
                <span className="inline-flex items-center gap-2">
                  <span className="font-body text-[11px] text-[#8A96A0]">{fmtDate(n.created_at)}</span>
                  {(user?.role === "admin" || n.author_id === user?.id) && (
                    <button
                      type="button"
                      onClick={() => deleteNote(n.id)}
                      title={t("common.delete")}
                      className="text-[#8A96A0] transition-colors hover:text-[#9E4747]"
                      data-testid={`match-note-delete-${n.id}`}
                    >
                      <Trash2 size={13} />
                    </button>
                  )}
                </span>
              </div>
              <p className="mt-1 whitespace-pre-wrap font-body text-sm text-[#3A464F]">{n.text}</p>
            </div>
          ))}
        </div>
        <div className="mt-3 flex flex-col gap-2 sm:flex-row">
          {canReview && (
          <>
          <textarea
            value={text}
            onChange={(e) => setText(e.target.value)}
            rows={2}
            placeholder={t("review.note_placeholder")}
            className="flex-1 resize-none rounded-md border border-[#D7DCE1] bg-white px-3 py-2 font-body text-sm text-[#1A2024] outline-none focus:border-[#4A7C59]"
            data-testid={`match-note-input-${anteId}-${postId}`}
          />
          <button
            type="button"
            disabled={busy || !text.trim()}
            onClick={addNote}
            className="self-end rounded-md bg-[#2E3A46] px-4 py-2 font-body text-sm font-medium text-white transition-colors hover:bg-[#1A2024] disabled:opacity-40"
            data-testid={`match-add-note-${anteId}-${postId}`}
          >
            {t("review.add_note")}
          </button>
          </>
          )}
        </div>
      </div>
    </div>
  );
}

export function MatchCard({ m, review, onReviewChange, priority = false, footer = null }) {
  const { t } = useI18n();
  const anteId = m.ante_mortem.id;
  const postId = m.post_mortem.id;
  const status = review?.status || "new";
  const [expanded, setExpanded] = useState(false);
  const [compareOpen, setCompareOpen] = useState(false);
  const collapsed = status === "excluded" && !expanded;
  const lastNote = (review?.notes || [])[(review?.notes || []).length - 1];

  if (collapsed) {
    return (
      <article
        className="rounded-lg border border-[#E2E8F0] bg-[#F8F9FA] p-4"
        data-testid={`match-card-${anteId}-${postId}`}
      >
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex flex-wrap items-center gap-2">
            <StatusBadge status="excluded" />
            <span className="font-body text-sm text-[#5C6873]">
              {t("matches.score")} {m.score}/100 · {m.ante_mortem.full_name || m.ante_mortem.reference_code || "—"} ↔ {m.post_mortem.reference_code || "—"}
            </span>
          </div>
          <button
            type="button"
            onClick={() => setExpanded(true)}
            className="font-body text-sm font-medium text-[#2E3A46] hover:underline"
            data-testid={`match-expand-${anteId}-${postId}`}
          >
            {t("review.show_details")}
          </button>
        </div>
        {lastNote && <p className="mt-2 font-body text-xs text-[#7A5A2A]">“{lastNote.text}” — {lastNote.author_name}</p>}
      </article>
    );
  }

  const borderCls = status === "excluded"
    ? "border-[#9E4747]/30"
    : m.timeline_warning
      ? "border-[#C08A2D]/50"
      : priority ? "border-[#9E4747]/30" : "border-[#E2E8F0]";
  const bgCls = priority && !m.timeline_warning && status !== "excluded" ? "bg-[#9E4747]/[0.03]" : "bg-white";

  return (
    <article
      className={`rounded-lg border ${borderCls} ${bgCls} p-6 shadow-sm`}
      data-testid={`match-card-${anteId}-${postId}`}
    >
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-wrap items-center gap-2">
          <Badge className="rounded-full bg-[#4A7C59]/10 font-body text-[#4A7C59] hover:bg-[#4A7C59]/15" data-testid="alert-score-badge">
            {t("matches.score")} {m.score}/100
          </Badge>
          <StatusBadge status={status} />
          <button
            type="button"
            onClick={() => setCompareOpen(true)}
            className="inline-flex items-center gap-1 rounded-full border border-[#E2E8F0] bg-white px-2.5 py-0.5 font-body text-[11px] font-medium text-[#2E3A46] transition-colors hover:bg-[#F8F9FA]"
            data-testid={`match-compare-btn-${anteId}-${postId}`}
          >
            <GitCompare size={12} /> {t("match.compare")}
          </button>
        </div>
        <div className="flex flex-wrap gap-1.5">
          {(m.score_breakdown?.length
            ? m.score_breakdown.filter((b) => b.field !== "circumstances")
            : m.matched_fields.map((f) => ({ field: f }))
          ).map((b) => (
            <span key={b.field} className="rounded-full border border-[#E2E8F0] bg-[#F8F9FA] px-2 py-0.5 font-body text-[11px] text-[#5C6873]" data-testid={`breakdown-${b.field}`}>
              {fieldLabel(t, b.field)}{b.points ? <span className="ml-1 font-semibold text-[#2E3A46]">+{b.points}</span> : null}
            </span>
          ))}
          {m.circumstance_hits?.length > 0 && (
            <span
              className="rounded-full border border-[#C99B4B]/40 bg-[#C99B4B]/10 px-2 py-0.5 font-body text-[11px] font-medium text-[#8a6a2e]"
              data-testid="circumstance-hits"
              title={m.circumstance_hits.join(", ")}
            >
              {t("matches.circ_hits")}: {m.circumstance_hits.join(", ")}
              {(() => {
                const pts = m.score_breakdown?.find((b) => b.field === "circumstances")?.points;
                return pts ? <span className="ml-1 font-semibold">+{pts}</span> : null;
              })()}
            </span>
          )}
        </div>
      </div>
      <Progress value={m.score} className="mt-3 h-1.5" />

      {m.score >= 85 && status !== "excluded" && (
        <div className="mt-3 rounded-md border border-[#4A7C59]/30 bg-[#4A7C59]/[0.06] px-4 py-3" data-testid="high-match-guidance">
          <p className="font-body text-sm font-semibold text-[#2E5540]">{t("match.highmatch_title")}</p>
          <p className="mt-1 font-body text-xs leading-relaxed text-[#3A5547]">{t("match.highmatch_body")}</p>
        </div>
      )}

      <CompareDialog m={m} open={compareOpen} onOpenChange={setCompareOpen} />

      <div className="mt-5 grid gap-6 md:grid-cols-2">
        <div>
          <div className="mb-2 font-body text-xs uppercase tracking-[0.16em] text-[#4A7C59]">{t("matches.ante")}</div>
          <MiniCard c={m.ante_mortem} kind="ante" />
        </div>
        <div>
          <div className="mb-2 font-body text-xs uppercase tracking-[0.16em] text-[#1F3A5F]">{t("matches.post")}</div>
          <MiniCard c={m.post_mortem} kind="post" />
        </div>
      </div>

      {m.timeline_warning && <TimelineWarning ante={m.ante_mortem} post={m.post_mortem} />}
      {footer}
      <MatchReview anteId={anteId} postId={postId} review={review} onChange={onReviewChange} canReview={m.can_review !== false} />

      {status === "excluded" && (
        <button
          type="button"
          onClick={() => setExpanded(false)}
          className="mt-3 font-body text-xs font-medium text-[#6B7F82] hover:underline"
          data-testid={`match-collapse-${anteId}-${postId}`}
        >
          {t("review.hide_details")}
        </button>
      )}
    </article>
  );
}


function LiaisonCard({ l }) {
  return (
    <div className="rounded-lg border border-[#E2E8F0] bg-white p-5 shadow-sm" data-testid={`liaison-block-${l.country}`}>
      <div className="flex items-center gap-2">
        <Badge variant="outline" className="rounded-full border-[#4A7C59]/30 bg-[#4A7C59]/10 font-body text-[#4A7C59]">{l.country}</Badge>
      </div>
      <div className="mt-3 flex items-start gap-2">
        <Building2 size={16} className="mt-0.5 text-[#6B7F82]" />
        <div className="font-heading text-base font-semibold text-[#1A2024]">{l.organization}</div>
      </div>
      <div className="mt-3 space-y-1.5 font-body text-sm text-[#5C6873]">
        {l.email && (
          <a href={`mailto:${l.email}`} className="flex items-center gap-2 hover:text-[#1A2024]" data-testid={`liaison-email-${l.country}`}>
            <Mail size={14} /> {l.email}
          </a>
        )}
        {l.phone && (
          <a href={`tel:${l.phone}`} className="flex items-center gap-2 hover:text-[#1A2024]" data-testid={`liaison-phone-${l.country}`}>
            <Phone size={14} /> {l.phone}
          </a>
        )}
        {l.note && <p className="pt-1 text-xs">{l.note}</p>}
      </div>
    </div>
  );
}

function FamilyStatusView() {
  const { t } = useI18n();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/family/status").then((r) => setData(r.data)).finally(() => setLoading(false));
  }, []);

  return (
    <div className="mx-auto max-w-3xl px-6 py-12" data-testid="family-status-page">
      <div className="rounded-2xl border border-[#E2E8F0] bg-gradient-to-b from-[#F2F6F3] to-white p-10 text-center shadow-sm">
        <span className="mx-auto grid h-14 w-14 place-items-center rounded-full bg-[#4A7C59]/12 text-[#4A7C59]">
          <ShieldCheck size={26} />
        </span>
        <h1 className="mt-5 font-heading text-2xl font-semibold text-[#1A2024]" data-testid="family-status-title">
          {t("family.status.title")}
        </h1>
        <p className="mx-auto mt-3 max-w-xl font-body text-base text-[#5C6873]">
          {t("family.status.body")}
        </p>
      </div>

      <div className="mt-10">
        <div className="flex items-center gap-2">
          <HeartHandshake size={18} className="text-[#6B7F82]" />
          <h2 className="font-heading text-lg font-semibold text-[#1A2024]">{t("family.status.contact_title")}</h2>
        </div>
        <p className="mt-1 font-body text-sm text-[#5C6873]">
          {t("family.status.contact_sub")}
        </p>

        <div className="mt-5 space-y-4">
          {loading && <div className="font-body text-sm text-[#5C6873]" data-testid="family-status-loading">{t("common.loading")}</div>}
          {!loading && data && data.liaisons.length > 0 &&
            data.liaisons.map((l) => <LiaisonCard key={l.id} l={l} />)}
          {!loading && data && data.liaisons.length === 0 && (
            <div className="rounded-lg border border-dashed border-[#E2E8F0] bg-[#F8F9FA] p-8 text-center font-body text-sm text-[#5C6873]" data-testid="family-status-no-liaison">
              {t("family.status.no_liaison")}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

const GROUP_PAGE = 10;
const PER_GROUP = 15;

function AuthorityMatchesView() {
  const { t } = useI18n();
  const [groups, setGroups] = useState([]);
  const [totalGroups, setTotalGroups] = useState(0);
  const [totalMatches, setTotalMatches] = useState(0);
  const [reviews, setReviews] = useState({});
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [minScore, setMinScore] = useState(40);
  const [hideExcluded, setHideExcluded] = useState(false);
  const [expanded, setExpanded] = useState(new Set());

  const keyOf = (m) => `${m.ante_mortem.id}::${m.post_mortem.id}`;

  const fetchPage = async (offset, replace) => {
    const r = await api.get(`/matches?min_score=${minScore}&group_offset=${offset}&group_limit=${GROUP_PAGE}&per_group=${PER_GROUP}`);
    const data = r.data;
    setTotalGroups(data.total_groups);
    setTotalMatches(data.total_matches);
    setReviews((prev) => {
      const map = replace ? {} : { ...prev };
      data.groups.forEach((g) => g.items.forEach((m) => { map[keyOf(m)] = m.review; }));
      return map;
    });
    setGroups((prev) => (replace ? data.groups : [...prev, ...data.groups]));
    if (replace) setExpanded(new Set(data.groups.length ? [data.groups[0].ante.id] : []));
  };

  useEffect(() => {
    setLoading(true);
    fetchPage(0, true).finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [minScore]);

  const loadMore = async () => {
    setLoadingMore(true);
    try { await fetchPage(groups.length, false); } finally { setLoadingMore(false); }
  };

  const toggleGroup = (id) =>
    setExpanded((prev) => {
      const n = new Set(prev);
      if (n.has(id)) n.delete(id); else n.add(id);
      return n;
    });

  const visibleGroups = groups
    .map((g) => ({ ...g, items: g.items.filter((m) => !(hideExcluded && (reviews[keyOf(m)]?.status || "new") === "excluded")) }))
    .filter((g) => g.items.length > 0);

  return (
    <div className="mx-auto max-w-6xl px-6 py-10" data-testid="matches-page">
      <p className="font-body text-xs uppercase tracking-[0.18em] text-[#6B7F82]">{t("matches.eyebrow")}</p>
      <h1 className="mt-1 font-heading text-3xl font-semibold text-[#1A2024]">{t("matches.title")}</h1>
      <p className="mt-2 max-w-2xl font-body text-sm text-[#5C6873]">
        {t("matches.sub")}
      </p>

      <div className="mt-6 flex flex-wrap items-center gap-6">
        <div className="flex items-center gap-3">
          <label className="font-body text-sm text-[#5C6873]">{t("matches.min_score")}</label>
          <input
            type="range" min={20} max={90} step={5} value={minScore}
            onChange={(e) => setMinScore(Number(e.target.value))}
            data-testid="matches-min-score"
          />
          <span className="font-body text-sm font-medium text-[#1A2024]" data-testid="matches-min-score-value">{minScore}</span>
        </div>
        {!loading && (
          <span className="rounded-full border border-[#E2E8F0] bg-[#F8F9FA] px-3 py-1 font-body text-sm font-medium text-[#2E3A46]" data-testid="matches-count">
            {totalMatches} {t("matches.count_label")}
          </span>
        )}
        <label className="flex cursor-pointer items-center gap-2 font-body text-sm text-[#5C6873]">
          <input
            type="checkbox"
            checked={hideExcluded}
            onChange={(e) => setHideExcluded(e.target.checked)}
            data-testid="matches-hide-excluded"
          />
          {t("review.hide_excluded")}
        </label>
      </div>

      <div className="mt-8 space-y-4">
        {loading && <div className="font-body text-sm text-[#5C6873]" data-testid="matches-loading">Loading matches…</div>}
        {!loading && visibleGroups.length === 0 && (
          <div className="rounded-lg border border-dashed border-[#E2E8F0] bg-[#F8F9FA] p-10 text-center" data-testid="matches-empty">
            <Sparkles className="mx-auto text-[#6B7F82]" />
            <h3 className="mt-2 font-heading text-lg font-semibold text-[#1A2024]">{t("matches.empty_title")}</h3>
            <p className="mx-auto mt-1 max-w-md font-body text-sm text-[#5C6873]">
              {t("matches.empty_sub")}
            </p>
          </div>
        )}

        {visibleGroups.map((g) => {
          const isOpen = expanded.has(g.ante.id);
          return (
            <section key={g.ante.id} data-testid={`match-group-${g.ante.id}`}>
              <button
                type="button"
                onClick={() => toggleGroup(g.ante.id)}
                className="mb-3 mt-8 flex w-full items-center gap-2 border-b border-[#E2E8F0] pb-2 text-left first:mt-0"
                data-testid={`match-group-toggle-${g.ante.id}`}
              >
                {isOpen
                  ? <ChevronDown size={18} className="shrink-0 text-[#6B7F82]" />
                  : <ChevronRight size={18} className="shrink-0 text-[#6B7F82]" />}
                <h2 className="font-heading text-lg font-semibold text-[#1A2024]">
                  {g.ante.full_name || g.ante.reference_code || "—"}
                </h2>
                <span className="rounded-full bg-[#2E3A46]/8 px-2 py-0.5 font-body text-xs font-medium text-[#2E3A46]" data-testid={`match-group-count-${g.ante.id}`}>
                  {g.total} {t("matches.count_label")}
                </span>
                <span className="ml-auto rounded-full bg-[#4A7C59]/10 px-2 py-0.5 font-body text-xs font-semibold text-[#4A7C59]">
                  {g.items[0].score}%
                </span>
              </button>
              {isOpen && (
                <div className="space-y-4">
                  {g.items.map((m, i) => (
                    <MatchCard
                      key={`${keyOf(m)}-${i}`}
                      m={m}
                      review={reviews[keyOf(m)]}
                      onReviewChange={(data) => setReviews((prev) => ({ ...prev, [keyOf(m)]: data }))}
                    />
                  ))}
                  {g.total > g.items.length && (
                    <p className="font-body text-xs text-[#6B7F82]" data-testid={`match-group-truncated-${g.ante.id}`}>
                      {t("matches.top_shown").replace("{shown}", g.items.length).replace("{total}", g.total)}
                    </p>
                  )}
                </div>
              )}
            </section>
          );
        })}

        {!loading && groups.length < totalGroups && (
          <div className="pt-4 text-center">
            <button
              type="button"
              onClick={loadMore}
              disabled={loadingMore}
              className="rounded-full border border-[#E2E8F0] bg-white px-5 py-2 font-body text-sm font-medium text-[#2E3A46] transition-base hover:bg-[#F8F9FA] disabled:opacity-50"
              data-testid="matches-load-more"
            >
              {loadingMore ? t("common.loading") : t("matches.load_more")}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

export default function MatchesPage() {
  const { user } = useAuth();
  if (user && user.role === "family") return <FamilyStatusView />;
  return <AuthorityMatchesView />;
}
